import { ALCHEMY_API_KEY, ZERION_NFT_CONTRACT } from "../../config/consts";
import { readContract } from "wagmi/actions";
import { erc721ABI } from "wagmi";
import * as Sentry from "@sentry/browser";
import { Address } from "@wagmi/core";
import { createAsyncAction, errorResult, successResult } from "pullstate";
import { mainnet } from "viem/chains";
import { Amount } from "../../types/amount";
import { GlobalStore } from "./GlobalStore";
import { getIsUserConnected } from "utils/userUtils";

export interface NftMetadataResponse {
  ownedNfts: [NftMetadata];
  totalCount: number;
  blockHash: string;
}

export interface NftMetadata {
  contract: { address: Address };
  id: {
    tokenId: `0x${string}`;
    tokenMetadata: {
      tokenType: string;
    };
  };
  title: string;
  description: string;
  tokenUri: {
    raw: string;
    gateway: string;
  };
  media: [
    {
      raw: string;
      gateway: string;
    }
  ];
  metadata: {
    name: string;
    description: string;
    image: string;
    external_url: string;
    attributes: [
      {
        value: string;
        trait_type: string;
      }
    ];
  };
  timeLastUpdated: string;
  contractMetadata: {
    name: string;
    symbol: string;
    totalSupply: string;
    tokenType: string;
  };
}

/*
- Added readContract to check if the user has a Zerion DNA NFT
- If the user has a Zerion DNA NFT then we run a fetch request to Alchemy /getNFTs we then store the lowest tokenId's image
- I think we can make this smarter I.E this fetch is pointless if the user has a ENS avatar as that takes precedence
 */
async function fetchZerionNft(userAddress: string): Promise<string> {
  const zerionNftBigIntBalance = await readContract({
    address: ZERION_NFT_CONTRACT,
    abi: erc721ABI,
    functionName: "balanceOf",
    args: [userAddress as `0x${string}`],
    chainId: mainnet.id,
  })
    .then(balanceBigInt => {
      const balance = new Amount(balanceBigInt.toString());
      return balance;
    })
    .catch(err => {
      console.error(`Balance error fetching Zerion NFT balance`, err);
      return Amount.ZERO;
    });
  if (zerionNftBigIntBalance.value.gt(0)) {
    return fetch(
      `https://eth-mainnet.g.alchemy.com/nft/v2/${ALCHEMY_API_KEY}/getNFTs?owner=${userAddress}&withMetadata=true&contractAddresses[]=${ZERION_NFT_CONTRACT}`
    )
      .then(response => {
        if (response.ok) {
          return response.json();
        } else {
          console.error(`Could not fetch Zerion NFT metadata. `, response.statusText);
          Sentry.captureException(`Could not fetch Token Metadata`, {
            extra: { error: response },
          });
          return {
            tokens: {},
          };
        }
      })
      .then((json: NftMetadataResponse) => {
        const tokenHexIds = json.ownedNfts.map(ownedNft => {
          return {
            id: new Amount(ownedNft.id.tokenId.toString()),
            image: ownedNft.metadata.image,
          };
        });
        // Returns the lowest tokenID image
        const zerionNftImage = tokenHexIds.reduce((acc, obj) =>
          obj.id < acc.id ? obj : acc
        ).image;
        return zerionNftImage;
      });
  }
  return "";
}

export const getZerionNft = createAsyncAction(
  async (address: string) => {
    try {
      const result = await fetchZerionNft(address);

      if (result) {
        return successResult(result);
      }
    } catch (e) {
      console.log(e);
      Sentry.captureException(`Could not get Zerion NFT metadata`, { extra: { event: e } });
      return errorResult([], `Could not get Zerion NFT metadata`, e);
    }
    Sentry.captureException(`Could not get Zerion NFT metadata`);
    return errorResult([], `Could not get Zerion NFT metadata`);
  },
  {
    postActionHook: ({ result, stores }) => {
      if (!result.error && getIsUserConnected()) {
        GlobalStore.update(s => {
          s.zerionNft = result.payload;
        });
      }
    },
  }
);
